// ============================================================================
// SPLIT CANVAS CIRCUIT BUILDER - React Component
// ============================================================================
// This replaces the canvas area section in circuitBuilder.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { CourseDomains } from '../../types/courseDomains';

import './splitCanvasArea.css'
import CustomButton from '../customs/customButton';

/**
 * Split Canvas Component
 * Renders hardware components on left, cloud components on right
 */
export const SplitCanvasArea = ({
    // Props from parent
    circuit,
    domain,
    canvasSettings,
    selectedComponent,
    selectedConnection,
    connectionMode,
    connectionStart,
    tempConnection,
    highlightedComponent, 
    canvasRef1, 
    canvasRef2,    
    canvasRef3,
    handleDrop,
    handleDragOver,
    handleCanvasMouseMove,
    handleCancelConnection,
    handleSelectComponent,
    handleComponentDragStart,
    handleComponentDragEnd,
    handleStartConnection,
    handleCompleteConnection,
    handleDeleteConnection,
    setPropertiesModalVisible,
    setWirePropertiesModalVisible,
    setSelectedConnection,
    setAlertConfig,
    setAlertVisible,  
    getPinPosition,
    getCrossCanvasPosition,
    onOpenFlowEditor,
}) => {

    // const [zoom, setZoom] = useState({ 
    //     hardware: 1, bridge: 1, cloud: 1, 
    //     training: 1, test: 1,
    //     flow: 1,
    //     build: 1, deploy: 1,        // ← AgenticAI canvases
    //     graph_wiring: 1,             // ← LangGraph sub-canvas
    // });
    // const [pan, setPan] = useState({ 
    //     hardware: {x:0,y:0}, bridge: {x:0,y:0}, cloud: {x:0,y:0},
    //     training: {x:0,y:0}, test: {x:0,y:0},
    //     flow: {x:0,y:0},
    //     build: {x:0,y:0}, deploy: {x:0,y:0},   // ← AgenticAI canvases
    //     graph_wiring: {x:0,y:0},                 // ← LangGraph sub-canvas
    // });
    // const [isPanning, setIsPanning] = useState({ 
    //     hardware: false, bridge: false, cloud: false,
    //     training: false, test: false,
    //     flow: false,
    //     build: false, deploy: false,   // ← AgenticAI canvases
    //     graph_wiring: false,            // ← LangGraph sub-canvas
    // });

    // ── Collect all canvas keys across ALL domains dynamically ─────────────────
    const allCanvasKeys = Object.values(CourseDomains).flatMap(domainCfg => [
        ...(domainCfg.canvases    || []).map(c => c.key),
        ...(domainCfg.subCanvases || []).map(c => c.key),
    ]);
    const uniqueCanvasKeys = [...new Set(allCanvasKeys)];

    const [zoom, setZoom] = useState(() =>
        Object.fromEntries(uniqueCanvasKeys.map(k => [k, 1]))
    );
    const [pan, setPan] = useState(() =>
        Object.fromEntries(uniqueCanvasKeys.map(k => [k, { x: 0, y: 0 }]))
    );
    const [isPanning, setIsPanning] = useState(() =>
        Object.fromEntries(uniqueCanvasKeys.map(k => [k, false]))
    );

    const [toolMode, setToolMode] = useState('select');
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });

    const domainConfig = CourseDomains[domain] ?? CourseDomains['IoT'];
    const canvasConfigs = domainConfig.canvases;
    // ── Map canvas keys to refs dynamically ────────────────────────────────────
    // Convention: canvas index 0 → canvasRef1
    //             canvas index 1 → canvasRef2
    //             canvas index 2 → canvasRef3
    //             sub-canvases   → canvasRef1
    const canvasRefsByKey = { canvasRef1, canvasRef2, canvasRef3 };

    const canvasRefs = Object.fromEntries(
        Object.values(CourseDomains).flatMap(domainCfg => [
            ...(domainCfg.canvases    || []).map(c => [c.key, canvasRefsByKey[c.refKey] ?? canvasRef1]),
            ...(domainCfg.subCanvases || []).map(c => [c.key, canvasRefsByKey[c.refKey] ?? canvasRef1]),
        ])
    );
    
    // Dispatch a resize event on every active canvas ref whenever zoom or pan changes
    // for the current domain — fully driven by CourseDomains, no hardcoded keys.
    useEffect(() => {
        const timer = setTimeout(() => {
            const activeRefs = new Set(
                (CourseDomains[domain]?.canvases ?? []).map(c => canvasRefsByKey[c.refKey] ?? canvasRef1)
            );
            activeRefs.forEach(ref => {
                if (ref?.current) ref.current.dispatchEvent(new Event('resize'));
            });
        }, 50);
        return () => clearTimeout(timer);
    // zoom and pan are plain objects — stringify so React sees value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, JSON.stringify(zoom), JSON.stringify(pan)]);
        
    const handleResetZoom = (section) => {
        setZoom(prev => ({ ...prev, [section]: 1 }));
        setPan(prev => ({ ...prev, [section]: { x: 0, y: 0 } }));
    };

    // Pan handlers
    const handleMouseDown = (e, section) => {
        if (e.button !== 0) return; // Only left click
        if (toolMode !== 'pan') return; // Only pan in pan mode
        setIsPanning(prev => ({ ...prev, [section]: true }));
        setPanStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e, section) => {
        if (!isPanning[section]) return;
        
        const deltaX = e.clientX - panStart.x;
        const deltaY = e.clientY - panStart.y;
        const maxPan = 100;
        const minPan = -300;
        
        setPan(prev => ({
            ...prev,
            [section]: {
                x: Math.max(minPan, Math.min(maxPan, prev[section].x + deltaX)),
                y: Math.max(minPan, Math.min(maxPan, prev[section].y + deltaY))
            }
        }));
        
        setPanStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = (section) => {
        setIsPanning(prev => ({ ...prev, [section]: false }));
    };

    // Wheel zoom handler
    const handleWheel = (e, section) => {
        if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            const direction = e.deltaY < 0 ? 'in' : 'out';
            handleZoom(section, direction);
        }
    };

    const handleZoom = (section, direction) => {
        const zoomFactor = direction === 'in' ? 1.2 : 0.8;
        const minZoom = 0.5;
        const maxZoom = 3;
        
        setZoom(prev => ({
            ...prev,
            [section]: Math.min(Math.max(prev[section] * zoomFactor, minZoom), maxZoom)
        }));
    };

    // Transform screen coordinates to canvas coordinates accounting for zoom/pan
    // const getCanvasCoordinates = useCallback((e, canvasRef, zoom, pan) => {
    //     if (!canvasRef?.current) return { x: e.clientX, y: e.clientY };
        
    //     const rect = canvasRef.current.getBoundingClientRect();
    //     const x = (e.clientX - rect.left) / zoom - pan.x;
    //     const y = (e.clientY - rect.top) / zoom - pan.y;
        
    //     return { x, y };
    // }, []);

    const CANVAS_PADDING = 1000; // must match the padding:'1000px' on circuit-canvas
    const getCanvasCoordinates = useCallback((e, canvasRef, zoom, pan) => {
        if (!canvasRef?.current) return { x: e.clientX, y: e.clientY };

        const rect = canvasRef.current.getBoundingClientRect();

        // rect is from circuit-canvas which:
        //   (a) is inside the scaled+translated canvas-content — so pan is already
        //       baked into rect.left/top; do NOT subtract pan again.
        //   (b) has padding:1000px — so the content origin is 1000px inside the rect;
        //       add CANVAS_PADDING back so coordinates are relative to content origin.
        const x = (e.clientX - rect.left) / zoom + CANVAS_PADDING;
        const y = (e.clientY - rect.top)  / zoom + CANVAS_PADDING;

        return { x, y };
    }, []);

    const wrappedHandleComponentDragStart = useCallback((e, component) => {
        const domainCanvases = CourseDomains[domain]?.canvases ?? [];
        const canvasConfig = domainCanvases.find(c => 
            c.categories.map(cat => cat.key).includes(component.category)
        );
        
        const fallbackKey = (CourseDomains[domain]?.canvases?.[0]?.key) ?? Object.keys(canvasRefs)[0];
        const canvasKey = canvasConfig?.key ?? fallbackKey;
        const canvasRef = canvasRefs[canvasKey];
        const currentZoom = zoom[canvasKey];
        const currentPan = pan[canvasKey];
        
        const coords = getCanvasCoordinates(e, canvasRef, currentZoom, currentPan);
        e.canvasX = coords.x;
        e.canvasY = coords.y;
        
        handleComponentDragStart(e, component);
    }, [getCanvasCoordinates, canvasRefs, zoom, pan, handleComponentDragStart]);

    const wrappedHandleComponentDragEnd = useCallback((e, component) => {
        const domainCanvases = CourseDomains[domain]?.canvases ?? [];
        const canvasConfig = domainCanvases.find(c => 
            c.categories.map(cat => cat.key).includes(component.category)
        );
        
        const fallbackKey = (CourseDomains[domain]?.canvases?.[0]?.key) ?? Object.keys(canvasRefs)[0];
        const canvasKey = canvasConfig?.key ?? fallbackKey;
        const canvasRef = canvasRefs[canvasKey];
        const currentZoom = zoom[canvasKey];
        const currentPan = pan[canvasKey];
        
        const coords = getCanvasCoordinates(e, canvasRef, currentZoom, currentPan);
        e.canvasX = coords.x;
        e.canvasY = coords.y;
        
        handleComponentDragEnd(e, component);
    }, [getCanvasCoordinates, canvasRefs, zoom, pan, handleComponentDragEnd]);

    const handleCanvasOutput = () => {
        console.log("Handle schematic or microchip code");
    }
    // ============================================================================
    // RENDER COMPONENT HELPER
    // ============================================================================
    
    // const renderComponent = (component, canvasType) => {
    //     const isBoard = component.category === 'boards';
    //     const domainCanvases = CourseDomains[domain]?.canvases ?? [];
    //     const isBridge = domainCanvases.find(c => c.key === 'bridge')?.categories.map(c => c.key).includes(component.category) ?? false;
    //     const isCloud = domainCanvases.find(c => c.key === 'cloud')?.categories.map(c => c.key).includes(component.category) ?? false;
    
    const renderComponent = (component, canvasType) => {
        const domainCanvases = CourseDomains[domain]?.canvases ?? [];
        const componentCanvasKey = domainCanvases
            .find(c => c.categories.map(cat => cat.key).includes(component.category))
            ?.key ?? domainCanvases[0]?.key ?? 'canvas';
        // const isPrimaryCanvas = componentCanvasKey === domainCanvases[0]?.key;
        const isPrimaryCanvas = component.category === 'boards';
        const isSelected = selectedComponent?.instanceId === component.instanceId;
        const isHighlighted = highlightedComponent === component.instanceId;  // ✅ Make sure this line exists
    
        return (
            <div
            key={component.instanceId}
            className={`
                placed-component 
                ${isSelected ? 'selected' : ''} 
                ${isHighlighted ? 'component-highlighted' : ''} 
                ${isPrimaryCanvas ? 'board-component' : ''} 
                canvas-${componentCanvasKey}
            `}
            style={{
                left: `${component.position?.x ?? 0}px`,
                top:  `${component.position?.y ?? 0}px`,
                zIndex: isSelected ? 1000 : (isHighlighted ? 999 : 1)  // ✅ Make sure this line exists
            }}
                draggable
                onDragStart={(e) => wrappedHandleComponentDragStart(e, component)}
                onDragEnd={(e) => wrappedHandleComponentDragEnd(e, component)}
                onClick={() => handleSelectComponent(component)}
            >
                {/* 3-Dot Menu Button */}
                <button
                    className="component-menu-btn"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSelectComponent(component);
                        setPropertiesModalVisible(true);
                    }}
                    >
                    ⋮
                </button>

                {/* Flow Editor Button — Node-RED Gateway only */}
                {component.isFlowEngine && onOpenFlowEditor && (
                    <button
                        // className="component-menu-btn component-flow-btn"
                        style={{
                            display: 'flex', alignItems: 'center', gap: '4px',
                            padding: '3px 10px',
                            background: '#3b82f6',
                            color: '#fff',
                            border: 'none', borderRadius: '5px',
                            fontSize: '12px',
                            cursor: 'pointer',
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectComponent(component);
                            onOpenFlowEditor(component);   // ← keep as-is
                        }}
                        title="Open Node-RED Flow Editor"
                    >
                        🔴 Flow
                    </button>
                )}

                <div className="component-icon-large">{component.icon}</div>
                <div className="component-label">{component.label}</div>
                
                {/* Pin list */}
                <div className="component-pins">
                {component.pinDetails && component.pinDetails
                    .filter(pin => pin.visible !== false)
                    .map((pin, idx) => (
                    <div 
                        key={idx} 
                        className={`component-pin ${pin.connectedTo ? 'pin-connected' : ''} ${connectionMode ? 'connection-active' : ''}`}
                        data-component-id={component.instanceId}
                        data-pin-name={pin.pinName}
                        onClick={(e) => {
                            e.stopPropagation();

                            if (connectionMode) {
                                if (!connectionStart) {
                                    handleStartConnection(component, pin.pinName);
                                } else {
                                    // ✅ FIX: capture the result
                                    const result = handleCompleteConnection(component, pin.pinName);
                                    // Handle both sync result and Promise
                                    const handleResult = (validationResult) => {
                                        if (!validationResult) { return; }

                                        if (validationResult.valid === false) {
                                            setAlertConfig({
                                                title: '❌ Connection Not Allowed',
                                                icon: '🚫',
                                                message: validationResult.message || 'This connection is not valid.',
                                                onConfirm: () => setAlertVisible(false)
                                            });
                                            setAlertVisible(true);
                                        } else if (validationResult.severity === 'warning') {
                                        setAlertConfig({
                                            title: '⚠️ Connection Warning',
                                            icon: '⚠️',
                                            message: validationResult.message || 'This connection has a warning.',
                                            onConfirm: () => setAlertVisible(false)
                                        });
                                        setAlertVisible(true);
                                        } else {
                                        }
                                    };

                                    // Support both sync and async handleCompleteConnection
                                    if (result && typeof result.then === 'function') {
                                        result.then(handleResult).catch(err => {
                                        console.error('🔌 [DEBUG] handleCompleteConnection threw:', err);
                                        });
                                    } else {
                                        handleResult(result);
                                    }
                                }
                            }
                        }}
                        title={pin.connectedTo ? `Connected to: ${pin.connectedTo}` : 'Click to connect'}
                    >
                        {pin.pinName}
                        {pin.connectedTo && <span className="pin-indicator">🔗</span>}
                    </div>
                    ))}
                </div>
            </div>
            );
    };

    // ============================================================================
    // RENDER CONNECTIONS HELPER
    // ============================================================================
    const renderConnections = useCallback((connections, section) => {
        return connections.map(conn => {
            const fromComp = circuit.components.find(c => c.instanceId === conn.from.componentInstanceId);
            const toComp = circuit.components.find(c => c.instanceId === conn.to.componentInstanceId);
            
            if (!fromComp || !toComp) return null;

            // Get logical pin positions (no transform needed - SVG is in same coordinate space)
            const fromPos = getPinPosition(fromComp, conn.from.pinName);
            const toPos = getPinPosition(toComp, conn.to.pinName);

            if (!fromPos || !toPos) return null;

            // Use logical coordinates directly - no manual transform!
            const fromIsLeft = fromPos.x < toPos.x;
            const startX = fromIsLeft ? fromPos.pinBoxRight : fromPos.pinBoxLeft;
            const startY = fromPos.y;
            const endX = fromIsLeft ? toPos.pinBoxLeft : toPos.pinBoxRight;
            const endY = toPos.y;

            return (
            <line
                key={conn.connectionId}
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke={conn.wireColor || '#000'}
                strokeWidth={conn.strokeWidth || 3}
                strokeDasharray={conn.strokeStyle === 'dashed' ? '8,4' : '0'}
                className={`connection-line ${selectedConnection?.connectionId === conn.connectionId ? 'selected' : ''}`}
                onClick={(e) => {
                e.stopPropagation();
                setSelectedConnection(conn);
                setWirePropertiesModalVisible(true);
                }}
                onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedConnection(conn);
                setAlertConfig({
                    title: 'Delete Wire',
                    icon: '🗑️',
                    message: `Delete connection from ${fromComp?.label}:${conn.from.pinName} to ${toComp?.label}:${conn.to.pinName}?`,
                    onConfirm: () => {
                    handleDeleteConnection();
                    setAlertVisible(false);
                    }
                });
                setAlertVisible(true);
                }}
                style={{ cursor: 'pointer' }}
            >
                <title>
                {`${conn.wireType || 'signal'}: ${fromComp?.label}:${conn.from.pinName} → ${toComp?.label}:${conn.to.pinName}\nLeft-click: Edit properties\nRight-click: Delete`}
                </title>
            </line>
            );
        });
    }, [circuit?.components, selectedConnection, getPinPosition, setSelectedConnection, setWirePropertiesModalVisible, setAlertConfig, setAlertVisible, handleDeleteConnection]);  
        
    return (
        // <div className="split-canvas-wrapper">
        <div 
            className={`
                split-canvas-wrapper 
                split-canvas-wrapper--${(domain || 'IoT').toLowerCase()} 
                split-canvas-wrapper--${CourseDomains[domain]?.canvasLayout ?? 'horizontal'}
            `}
        >
            {/* GLOBAL TOOL MODE SELECTOR */}
            <div className="global-tool-selector">
                <button 
                    className={`tool-btn ${toolMode === 'select' ? 'active' : ''}`}
                    onClick={() => setToolMode('select')}
                    title="Select Tool (V)"
                >
                    <span className="tool-icon">↖</span>
                </button>
                <button 
                    className={`tool-btn ${toolMode === 'pan' ? 'active' : ''}`}
                    onClick={() => setToolMode('pan')}
                    title="Pan Tool (H)"
                >
                    <span className="tool-icon">✋</span>
                </button>
            </div>
            
                {canvasConfigs.map((config, canvasIndex) => (
                    <div key={config.key} className={`canvas-section ${config.key}-canvas`}>     
                    <div className="canvas-section-header">
                        <div className="canvas-section-title">
                            <span className="canvas-section-icon">{config.icon}</span>
                            {config.header}
                            <span className="canvas-section-badge">{config.subHeader}</span>
                        </div>
                        {config.output && (<CustomButton
                            buttonTitle={config.output==='schematic' ? "Schematic" : "Microchip"}
                            action={handleCanvasOutput}
                            bgColor={"#102cb9"}
                            // disabled={!isCompiled || (!isRunning && !simulator)}
                        />)}
                    </div>

                    <div 
                        className={`canvas-viewport ${isPanning[config.key] ? 'panning' : ''}`}
                        data-tool-mode={toolMode}
                        onMouseDown={(e) => handleMouseDown(e, config.key)}
                        onMouseMove={(e) => handleMouseMove(e, config.key)}
                        onMouseUp={() => handleMouseUp(config.key)}
                        onMouseLeave={() => handleMouseUp(config.key)}
                        onWheel={(e) => handleWheel(e, config.key)}
                    >
                        {/* ✅ canvas-content was missing */}
                        <div className="canvas-content"
                            style={{ 
                                transform: `scale(${zoom[config.key]}) translate(${pan[config.key].x}px, ${pan[config.key].y}px)`,
                                width: `${100 / zoom[config.key]}%`,
                                height: `${100 / zoom[config.key]}%`
                            }}
                        >
                            <div
                                ref={canvasRefs[config.key]}
                                className={`circuit-canvas ${canvasSettings.showGrid ? 'show-grid' : ''} ${connectionMode ? 'connection-mode' : ''}`}
                                onDrop={handleDrop}
                                onDragOver={handleDragOver}
                                onMouseMove={handleCanvasMouseMove}
                                onContextMenu={(e) => { e.preventDefault(); handleCancelConnection(); }}
                                style={{
                                    backgroundColor: config.backgroundColor,
                                    backgroundSize: `${canvasSettings.gridSize}px ${canvasSettings.gridSize}px`,
                                    padding: '1000px'
                                }}
                            >
                                {circuit?.components
                                    ?.filter(c => config.categories.map(c => c.key).includes(c.category))
                                    .map(component => renderComponent(component, config.key))}

                                {circuit?.components?.filter(c => config.categories.map(c => c.key).includes(c.category)).length === 0 && (
                                    <div className="canvas-empty-state">
                                        <p>{config.emptyMessage}</p>
                                    </div>
                                )}
                            </div>

                            {/* ✅ SVG connections layer was missing */}
                            <svg className="connections-layer" style={{
                                position: 'absolute', top: 0, left: 0,
                                width: '100%', height: '100%', pointerEvents: 'none'
                            }}>
                                {renderConnections(
                                    circuit?.connections?.filter(conn => {
                                        const fromComp = circuit.components.find(c => c.instanceId === conn.from.componentInstanceId);
                                        const toComp = circuit.components.find(c => c.instanceId === conn.to.componentInstanceId);
                                        return fromComp && toComp &&
                                            // config.categories.includes(fromComp.category) &&
                                            // config.categories.includes(toComp.category);
                                            config.categories.map(c => c.key).includes(fromComp.category) && 
                                            config.categories.map(c => c.key).includes(toComp.category)
                                    }) || [],
                                    config.key
                                )}

                                {/* Temp connection line */}
                                {tempConnection && tempConnection.mousePosition && connectionStart &&
                                // config.categories.includes(connectionStart.component.category) && (
                                config.categories.map(c => c.key).includes(connectionStart.component.category) && (
                                    (() => {
                                        const startPos = getPinPosition(connectionStart.component, connectionStart.pinName);
                                        if (!startPos) return null;
                                        const startX = tempConnection.mousePosition.x > startPos.x
                                            ? startPos.pinBoxRight : startPos.pinBoxLeft;
                                        return (
                                            <line
                                                x1={startX} y1={startPos.y}
                                                x2={tempConnection.mousePosition.x} y2={tempConnection.mousePosition.y}
                                                stroke="#3b82f6" strokeWidth="3" strokeDasharray="5,5"
                                                className="temp-connection-line"
                                            />
                                        );
                                    })()
                                )}
                            </svg>
                        </div>
                    </div>

                    {/* ✅ Zoom controls were missing */}
                    <div className={`
                        canvas-zoom-controls ${canvasIndex === 0 && 
                        (CourseDomains[domain]?.canvasLayout ?? 
                        'horizontal') === 'horizontal' ? 
                        'canvas-zoom-controls--left' : ''}
                    `}>
                        <button className="zoom-btn" onClick={() => handleZoom(config.key, 'in')}>+</button>
                        <div className="zoom-level">{Math.round(zoom[config.key] * 100)}%</div>
                        <button className="zoom-btn" onClick={() => handleZoom(config.key, 'out')}>−</button>
                        <button className="zoom-btn" onClick={() => handleResetZoom(config.key)}>⟲</button>
                    </div>

                </div>
            ))}
            
        </div>
    );
};

export default SplitCanvasArea;